import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { ticketStandardsQuery, type TicketPolicyContent } from '../api/easeedesk'
import { Spinner } from '../ui/Spinner'
import './TicketStandards.css'

/**
 * Fila "Standards" - `policies/easeedesk_policies.php`.
 *
 * Trei alegeri si un text: categoria, subcategoria si al cui e standardul - al
 * furnizorului sau al clientului. Fila se deschide pe categoria si subcategoria
 * TICHETULUI, ca sa nu caute nimeni cu mana ce scrie deja pe el.
 *
 * O SINGURA CERERE. In portal erau trei, una dupa alta: fila cerea categoriile,
 * apoi `policies/getSubcategories`, apoi `policies/show_policies` - si a treia
 * intorcea HTML gata facut, deci textul politicii nu se putea nici cauta, nici
 * numara, nici desena altfel.
 */

export function TicketStandards({ id }: { id: number }) {
  const [chosen, setChosen] = useState({ category: 0, subcategory: 0, scope: 'provider' })
  const { data, error } = useQuery(ticketStandardsQuery(id, chosen.category, chosen.subcategory, chosen.scope))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {error.message}
      </div>
    )
  }
  if (!data) return <Spinner />

  if (data.categories.length === 0) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> No IT standard has been written yet.
      </div>
    )
  }

  return (
    <div className="st-wrap">
      <div className="row">
        <div className="col-md-4">
          <label className="control-label" htmlFor={`st-cat-${id}`}>
            Category:
          </label>
          <select
            id={`st-cat-${id}`}
            className="form-control"
            value={data.categoryId}
            /* Categoria schimbata sterge subcategoria: cea veche e din alt
               parinte, si serverul ar cadea oricum inapoi pe prima din lista. */
            onChange={(ev) => setChosen({ ...chosen, category: Number(ev.target.value), subcategory: 0 })}
          >
            {data.categories.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="control-label" htmlFor={`st-sub-${id}`}>
            Subcategory:
          </label>
          <select
            id={`st-sub-${id}`}
            className="form-control"
            value={data.subcategoryId}
            onChange={(ev) => setChosen({ ...chosen, category: data.categoryId, subcategory: Number(ev.target.value) })}
          >
            {data.subcategories.map((one) => (
              <option key={one.id} value={one.id}>
                {one.name}
              </option>
            ))}
          </select>
        </div>

        <div className="col-md-4">
          <label className="control-label" htmlFor={`st-scope-${id}`}>
            On the:
          </label>
          <select
            id={`st-scope-${id}`}
            className="form-control"
            value={data.scope}
            onChange={(ev) =>
              setChosen({ category: data.categoryId, subcategory: data.subcategoryId, scope: ev.target.value })
            }
          >
            <option value="provider">Service Provider</option>
            <option value="company">Company</option>
          </select>
        </div>
      </div>

      <div className="st-content">
        <Content content={data.content} />
      </div>
    </div>
  )
}

function Content({ content }: { content: TicketPolicyContent | null }) {
  if (content === null) {
    return (
      <div className="alert alert-info" role="alert">
        <i className="fas fa-info-circle" aria-hidden="true"></i> Pick a subcategory to read its standard.
      </div>
    )
  }

  const written =
    content.standard.trim() !== '' || content.configuration.trim() !== '' || content.exception.trim() !== ''

  if (!written && content.attachments.length === 0) {
    /* Un panou gol si o politica nescrisa arata la fel, si sunt doua lucruri
       diferite: al doilea e o treaba de facut, nu o defectiune. */
    return (
      <div className="alert alert-warning" role="alert">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> Nothing has been written for this subcategory
        yet.
      </div>
    )
  }

  return (
    <>
      <Section title="Standard" html={content.standard} />
      <Section title="Configuration" html={content.configuration} />
      <Section title="Exceptions" html={content.exception} />

      {content.attachments.length > 0 && (
        <section className="st-section">
          <h5 className="bordered">Files</h5>
          <ul className="st-files">
            {content.attachments.map((file) => (
              <li key={file.id}>
                <a href={file.url} target="_blank" rel="noopener">
                  <i className="fa fa-paperclip" aria-hidden="true"></i> {file.file_name}
                </a>
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  )
}

function Section({ title, html }: { title: string; html: string }) {
  if (html.trim() === '') return null

  return (
    <section className="st-section">
      <h5 className="bordered">{title}</h5>
      {/*
       * Textul politicii e scris in editorul portalului si tinut ca HTML - e
       * continut de-al casei, scris de un om cu drept de editare, nu de pe
       * internet. Se deseneaza asa cum e, ca in portal.
       */}
      <div className="st-html" dangerouslySetInnerHTML={{ __html: html }} />
    </section>
  )
}
